import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Trash2 } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';

interface User {
    id: string;
    email: string;
    full_name?: string;
    is_active: boolean;
    is_verified: boolean;
    is_superuser: boolean;
    cost_center_id?: string;
    language: string;
    created_at?: string;
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

export default function UsersPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { t } = useI18n();
    const [users, setUsers] = useState<User[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadUsers();
        loadCostCenters();
    }, []);

    // Load users
    const loadUsers = async () => {
        try {
            const response = await apiFetch('/api/users/');
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            } else {
                const errorData = await response.json();
                setError(errorData.detail || t('users.loadFailed', 'admin'));
            }
        } catch (error) {
            console.error('Error loading users:', error);
            setError(t('users.loadFailed', 'admin'));
        } finally {
            setLoading(false);
        }
    };
    // Load cost centers
    const loadCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(data);
            }
        } catch (error) {
            console.error('Error loading cost centers:', error);
        }
    };


    // Navigation functions
    const handleCreateUser = () => {
        navigate({ to: '/admin/users/new' as any});
    };

    const handleEditUser = (user: User) => {
        navigate({ to: `/admin/users/${user.id}`});
    };

    // Delete user
    const deleteUser = async (id: string, email: string) => {
        if (!confirm(t('users.deleteConfirm', 'admin', { email }))) {
            return;
        }

        try {
            const response = await apiFetch(`/api/users/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await loadUsers();
            } else {
                const errorData = await response.json();
                setError(errorData.detail || t('users.deleteFailed', 'admin'));
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            setError(t('users.deleteFailed', 'admin'));
        }
    };

    if (error && !users.length) {
        return (
            <Layout>
                <div className="text-center py-12">
                    <div className="text-red-600 text-lg font-medium">{error}</div>
                    <p className="text-gray-500 mt-2">
                        {t('users.superuserRequired', 'admin')}
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            {t('users.title', 'admin')}
                        </h1>
                        <p className="text-muted-foreground">
                            {t('users.subtitle', 'admin')}
                        </p>
                    </div>
                    <Button onClick={handleCreateUser}>
                        {t('users.addUser', 'admin')}
                    </Button>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Users Table */}
                {loading ? (
                    <div className="space-y-3">
                        {[...Array(5)].map((_, i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('forms.email_address', 'common')}</TableHead>
                                <TableHead>{t('users.fullName', 'admin')}</TableHead>
                                <TableHead>{t('settings.account.location', 'common')}</TableHead>
                                <TableHead>{t('users.status', 'admin')}</TableHead>
                                <TableHead>{t('users.verified', 'admin')}</TableHead>
                                <TableHead>{t('users.language', 'admin')}</TableHead>
                                <TableHead>{t('users.superuser', 'admin')}</TableHead>
                                <TableHead>{t('actions.actions', 'common')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                                        {t('users.noUsersFound', 'admin')}{' '}
                                        <Button
                                            variant="outline"
                                            onClick={handleCreateUser}
                                            className="ml-2"
                                        >
                                            {t('users.addFirstUser', 'admin')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((user) => {
                                    const costCenter = costCenters.find(cc => cc.id === user.cost_center_id);

                                    return (
                                        <TableRow key={user.id}>
                                            <TableCell className="font-medium">{user.email}</TableCell>
                                             <TableCell>
                                                {user.full_name || (
                                                    <span className="text-muted-foreground">
                                                        {t('users.noName', 'admin')}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {costCenter?.name || (
                                                        <span className="text-muted-foreground">
                                                            {t('settings.account.noLocation', 'common')}
                                                        </span>
                                                    )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={user.is_active ? "default" : "secondary"}>
                                                    {user.is_active
                                                        ? t('status.active', 'common')
                                                        : t('status.inactive', 'common')}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={user.is_verified ? "default" : "secondary"}>
                                                    {user.is_verified
                                                        ? t('users.verifiedStatus', 'admin')
                                                        : t('users.unverifiedStatus', 'admin')}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="uppercase">
                                                {user.language}
                                            </TableCell>

                                            <TableCell>
                                                <Badge variant={user.is_superuser ? "default" : "secondary"}>
                                                    {user.is_superuser
                                                        ? t('users.yes', 'admin')
                                                        : t('users.no', 'admin')}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex space-x-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleEditUser(user)}
                                                    >
                                                        {t('actions.edit', 'common')}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => deleteUser(user.id, user.email)}
                                                        className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/users/')({
  component: UsersPage,
})
